import { useMutation } from "@apollo/client";
import { MenuItemProps, Text } from "@chakra-ui/react";
import { FragmentType, getFragmentData, gql } from "__generated__/gql";
import { useActiveBrandIdContext } from "utils/apollo/hooks/brand";
import { ADMIN_CONTEXT } from "utils/apollo/hooks/admin";
import DeleteConfirmMenuItem from "components/Shared/ConfirmModal/DeleteConfirmMenuItem";
import { removeDeletedRecords } from "utils/apollo/cacheHelpers";
import { assertPresence } from "utils/assertions";

const fragment = gql(/* GraphQL */ `
  fragment RetailerDeleteMenuItemFragment on Retailer {
    id
    nameWithUniqueSuffix
    isCustom
  }
`);

const mutation = gql(/* GraphQL */ `
  mutation DeleteRetailer($retailerId: UUID!) {
    deleteRetailer(input: { id: $retailerId }) {
      retailer {
        id
        nameWithUniqueSuffix
        ...RetailerDeleteMenuItemFragment
      }
    }
  }
`);

type Props = Pick<MenuItemProps, "isDisabled"> & {
  isAdmin?: boolean;
  onCompleted?: () => void;
  retailer: FragmentType<typeof fragment> | null | undefined;
};

export default function DeleteMenuItem({
  isAdmin,
  isDisabled,
  onCompleted,
  retailer: retailerFragment,
}: Props) {
  const retailer = getFragmentData(fragment, retailerFragment);

  const brandContext = useActiveBrandIdContext();

  const mutationTuple = useMutation(mutation, {
    context: isAdmin ? ADMIN_CONTEXT : brandContext,
    variables: retailer ? { retailerId: retailer.id } : undefined,
    update: (cache, result) => {
      const deletedRetailer = result.data?.deleteRetailer?.retailer;
      assertPresence(deletedRetailer);
      removeDeletedRecords({
        cache,
        baseFieldName: "retailers",
        deletedRecords: [deletedRetailer],
      });
    },
    onCompleted: () => {
      onCompleted?.();
    },
  });

  const customLabel = retailer?.isCustom ? "Custom" : "Default";

  return (
    <DeleteConfirmMenuItem
      label={`Delete ${customLabel} Retailer…`}
      menuItemProps={{ isDisabled: isDisabled || !retailer }}
      mutation={mutationTuple}
      confirmText={
        <>
          <Text>
            Deleting a retailer will permanently remove it from the directory,
            as well as any data associated with it. This cannot be undone and
            should always be used with caution. In most cases, archiving the
            retailer is a better option. If this retailer has already been used,
            including in sales plans or promotions, attempting to delete it will
            fail.
          </Text>
          <Text mt={3}>
            Are you sure you want to delete the {customLabel.toLowerCase()}{" "}
            retailer, <strong>{retailer?.nameWithUniqueSuffix}</strong>?
          </Text>
        </>
      }
    />
  );
}
